import { Country, IdentityCheckStatus } from '@afterpay/types'
import { TrackingEvent } from '../../../model/amplitude'
import { IdentityErrorCode } from '@afterpaytouch/portal-api/identity'
import { ConsumerAccountIdentitiesResponse } from '@afterpaytouch/portal-api/consumer/account/types'
import { Route } from '../../../config/router'
import { isCountryNeedsBiometricVerification, isCountryUsePersonaFlow, shouldStartBiometric, getIdentityVerificationFailedRoute } from '../../../model'
export type StepOneAction = 'none' | 'unsuitability' | 'affordability' | 'idv' | Route

export interface StepCtaProps {
  label: string
  onClick: () => void
}

export interface StepStatus {
  task: string
  subtask: string
  isCompleted: boolean
  cta: StepCtaProps
}

export interface GetStepOneStatusModelArgs {
  creditLimitErrorCode?: IdentityErrorCode | null
  isUnsuitabilityRequiredError: (code?: IdentityErrorCode | null) => boolean
  isAffordabilityRequiredError: (code?: IdentityErrorCode | null) => boolean
  identityCheckStatus: IdentityCheckStatus
  country: Country
  identitiesData?: ConsumerAccountIdentitiesResponse
  manualIdvEnabled?: boolean
}

export interface StepOneStatusModel {
  taskKey: string
  subtaskKey: string
  isCompleted: boolean
  action: StepOneAction
  logEvent?: { event: string; payload?: Record<string, unknown> }
  context?: string
}

export const getStepOneStatusModel = ({
  creditLimitErrorCode,
  isUnsuitabilityRequiredError,
  isAffordabilityRequiredError,
  identityCheckStatus,
  country,
  identitiesData,
  manualIdvEnabled,
}: GetStepOneStatusModelArgs): StepOneStatusModel => {
  const identityCheckStatusUpdated = identitiesData?.identityCheckStatus ?? identityCheckStatus
  if (creditLimitErrorCode !== null && creditLimitErrorCode !== undefined && isUnsuitabilityRequiredError(creditLimitErrorCode)) {
    return {
      taskKey: 'index:welcome:steps:unsuitability:task',
      subtaskKey: 'index:welcome:steps:unsuitability:subtask',
      isCompleted: false,
      action: 'unsuitability',
      logEvent: { event: TrackingEvent.VIEWED_COMPLETE_NOW_ON_HOME_PAGE },
    }
  }

  if (creditLimitErrorCode !== null && creditLimitErrorCode !== undefined && isAffordabilityRequiredError?.(creditLimitErrorCode)) {
    return {
      taskKey: 'index:welcome:steps:unsuitability:task',
      subtaskKey: 'index:welcome:steps:unsuitability:subtask',
      isCompleted: false,
      action: 'affordability',
      logEvent: { event: TrackingEvent.VIEWED_COMPLETE_NOW_ON_HOME_PAGE },
      context: 'affordability',
    }
  }

  if (
    identitiesData != null &&
    identityCheckStatusUpdated === IdentityCheckStatus.NOT_VERIFIED &&
    isCountryNeedsBiometricVerification(country) &&
    !shouldStartBiometric(identitiesData, isCountryUsePersonaFlow(country)) &&
    identitiesData.identityCheckMetadata?.profileCheckAttemptsLeft === false
  ) {
    return {
      taskKey: 'index:welcome:steps:verify:task-being-verified',
      subtaskKey: 'index:welcome:steps:verify:subtask-being-verified',
      isCompleted: true,
      action: 'none',
    }
  }

  if (identityCheckStatusUpdated === IdentityCheckStatus.VERIFIED) {
    return {
      taskKey: 'index:welcome:steps:verify:task-completed',
      subtaskKey: 'index:welcome:steps:verify:subtask-completed',
      isCompleted: true,
      action: 'none',
    }
  }

  if (identityCheckStatusUpdated === IdentityCheckStatus.FAILED) {
    return {
      taskKey: 'index:welcome:steps:verify:task',
      subtaskKey: 'index:welcome:steps:verify:subtask-failed',
      isCompleted: false,
      action: getIdentityVerificationFailedRoute(manualIdvEnabled ?? false),
      logEvent: { event: TrackingEvent.VIEWED_VERIFY_NOW_ON_HOME_PAGE, payload: { status: identityCheckStatusUpdated } },
    }
  }

  if (identityCheckStatusUpdated === IdentityCheckStatus.NOT_REQUIRED) {
    return {
      taskKey: 'index:welcome:steps:verify:task-not-required',
      subtaskKey: 'index:welcome:steps:verify:task-not-required-detail',
      isCompleted: true,
      action: 'none',
    }
  }

  if (identityCheckStatusUpdated === IdentityCheckStatus.NOT_VERIFIED || identityCheckStatusUpdated === IdentityCheckStatus.PARTIAL_VERIFIED) {
    return {
      taskKey: 'index:welcome:steps:verify:task',
      subtaskKey: 'index:welcome:steps:verify:subtask',
      isCompleted: false,
      action: 'idv',
      logEvent: { event: TrackingEvent.VIEWED_VERIFY_NOW_ON_HOME_PAGE, payload: { status: identityCheckStatusUpdated } },
    }
  }

  return {
    taskKey: 'index:welcome:steps:verify:task-not-required',
    subtaskKey: 'index:welcome:steps:verify:task-not-required-detail',
    isCompleted: true,
    action: 'none',
  }
}
